// M2 경영 렌즈 시각화 (pricing-corridor Design §6) — 환율 민감도 곡선 + 국가비교 막대.
// 4렌즈는 calc.ts 결과의 "투영"일 뿐(§1): 여기서 자체 계산하지 않고 상위(ManagementDeck)에서
// tierUnitPrice(retroactive 표준) + marginPair 로 계산된 값만 그린다.
// 가격 모델(2026-07-16 정정): 최종 대수 구간 단가를 전량 적용 — 혼합 평균(blended) 아님.
// 마진 수치는 calc.test.ts 로 잠금(13,000 → 69.23% / flat 10,000 → 60.00% @fx 1500).
// 세율·관세 수치 없음(DP-3). 팔레트는 MANIFEST 디자인 토큰(tailwind.config.js) 과 동일 hex.
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

// MANIFEST 토큰 (잉크 네이비 + 브라스 시그널 + 의미색) — tailwind.config.js 와 동일.
const T = {
  graphite: "#16222f",
  muted: "#566579",
  line: "#dde3ec",
  lineStrong: "#c7d0dc",
  surface: "#ffffff",
  brass: "#c67f2c",
  ink: "#0e2036",
  risk: "#c23a33",
};

/** 국가별 마진곡선 한 줄 — margins[i] 는 fxAxis[i] 환율에서의 단가마진율(%, 0~100). */
export interface FxSeries {
  name: string;
  color: string;
  margins: number[];
}

/**
 * ① 환율 민감도 곡선 — x=환율(₩/$), y=단가 마진율(%). 국가별 라인.
 * 원가는 KRW·매출은 USD 이므로 환율↓ = costUsd↑ = 마진 잠식. trigger 아래(붉은 눈금)는 재협상 구간.
 * currentFx = 현재 슬라이더 값 → 가장 가까운 그리드 점을 마커로 강조(슬라이더↔차트 연결).
 */
export function FxSensitivityChart({
  fxAxis,
  series,
  trigger,
  currentFx,
}: {
  fxAxis: number[];
  series: FxSeries[];
  trigger: number;
  currentFx: number;
}): JSX.Element {
  let curIdx = 0;
  let best = Infinity;
  fxAxis.forEach((f, i) => {
    const d = Math.abs(f - currentFx);
    if (d < best) {
      best = d;
      curIdx = i;
    }
  });
  const belowTrigger = (i: number) => fxAxis[i] != null && (fxAxis[i] as number) < trigger;

  return (
    <Line
      data={{
        labels: fxAxis.map((f) => "₩" + f.toLocaleString("ko-KR")),
        datasets: series.map((s) => ({
          label: s.name,
          data: s.margins,
          borderColor: s.color,
          backgroundColor: s.color,
          borderWidth: 2,
          pointRadius: (ctx: { dataIndex: number }) => (ctx.dataIndex === curIdx ? 5 : 0),
          pointHoverRadius: 6,
          pointBackgroundColor: s.color,
          pointBorderColor: T.surface,
          pointBorderWidth: 2,
          pointHitRadius: 12,
          tension: 0.15,
        })),
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: (ctx: { index: number }) => (belowTrigger(ctx.index) ? T.risk : T.muted),
              font: (ctx: { index: number }) => ({ size: 10, weight: belowTrigger(ctx.index) ? ("bold" as const) : ("normal" as const) }),
              maxTicksLimit: 8,
            },
            title: { display: true, text: `환율 ₩/$ · 붉은 눈금 <₩${trigger.toLocaleString("ko-KR")} 재협상`, color: T.muted, font: { size: 10 } },
          },
          y: {
            grid: { color: T.line },
            border: { display: false },
            ticks: { color: T.muted, font: { size: 10 }, callback: (v) => v + "%" },
            title: { display: true, text: "단가 마진율", color: T.muted, font: { size: 10 } },
          },
        },
        plugins: {
          legend: { position: "bottom", labels: { color: T.graphite, boxWidth: 10, boxHeight: 10, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              title: (items) => {
                const i = items[0]?.dataIndex ?? 0;
                return "환율 " + (items[0]?.label ?? "") + (belowTrigger(i) ? "  ⚠ 재협상 구간" : "");
              },
              label: (ctx) => ` ${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(1)}%`,
            },
          },
        },
      }}
    />
  );
}

/**
 * ② 국가비교 막대 — 막대=블렌디드(USD, 좌축, 브라스), 막대=단가 마진율(%, 우축, 잉크). 툴팁에 국가 격차.
 * 현재 대수·환율 슬라이더 값 기준. flat 국가는 계단 없이 고정단가.
 */
export function CountryComparisonChart({
  labels,
  unitPrice,
  marginPct,
  gapPct,
}: {
  labels: string[];
  unitPrice: number[];
  marginPct: number[];
  gapPct: number[];
}): JSX.Element {
  return (
    <Bar
      data={{
        labels,
        datasets: [
          {
            label: "구간 단가 USD/대",
            data: unitPrice,
            backgroundColor: T.brass,
            borderColor: T.surface,
            borderWidth: 1,
            maxBarThickness: 30,
            borderRadius: 3,
            yAxisID: "y",
          },
          {
            label: "단가 마진율 %",
            data: marginPct,
            backgroundColor: T.ink,
            borderColor: T.surface,
            borderWidth: 1,
            maxBarThickness: 30,
            borderRadius: 3,
            yAxisID: "y1",
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: { grid: { display: false }, ticks: { color: T.graphite, font: { size: 11 } } },
          y: {
            position: "left",
            grid: { color: T.line },
            border: { display: false },
            ticks: { color: T.muted, font: { size: 10 }, callback: (v) => "$" + Number(v).toLocaleString("en-US") },
            title: { display: true, text: "구간 단가 USD/대", color: T.brass, font: { size: 10 } },
          },
          y1: {
            position: "right",
            min: 0,
            max: 100,
            grid: { display: false },
            border: { display: false },
            ticks: { color: T.muted, font: { size: 10 }, callback: (v) => v + "%" },
            title: { display: true, text: "마진%", color: T.ink, font: { size: 10 } },
          },
        },
        plugins: {
          legend: { position: "bottom", labels: { color: T.graphite, boxWidth: 10, boxHeight: 10, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                ctx.dataset.label === "단가 마진율 %"
                  ? ` 마진율: ${Number(ctx.parsed.y).toFixed(1)}%`
                  : ` 블렌디드: $${Number(ctx.parsed.y).toLocaleString("en-US")}`,
              footer: (items) => {
                const i = items[0]?.dataIndex ?? 0;
                const g = gapPct[i] ?? 0;
                return g > 0.05 ? `국가 격차 +${g.toFixed(1)}% (최저 대비)` : "최저 기준";
              },
            },
          },
        },
      }}
    />
  );
}
